<script setup lang="ts">
definePageMeta({
  pageTransition: false
})
import {
  onMounted,
  ref,
  type Ref,
  onUnmounted,
  type ComponentPublicInstance,
  reactive,
  computed,
  inject,
  nextTick
} from 'vue'
import BaseLayout from '@/components/business/layout/BaseLayout.vue'
import MFooter from '@/components/business/memorize/MFooter.vue'
import MemorizeComment from '@/components/business/memorize/MemorizeComment.vue'
import MemorizeSkeleton from '@/components/business/skeleton/MemorizeSkeleton.vue'
import RAction from '@/components/base/RAction/index.vue'
import GiftPanel from '@/components/business/memorize/gift/GiftPanel.vue'
import SubscribeDrawer from '@/components/business/modal/SubscribeDrawer.vue'
import ThankDialog from '@/components/base/ThankDialog/ThankDialog.vue'
import ReviceThankDialog from '@/components/base/ThankDialog/ReviceThankDialog.vue'
import RediectToLoginDialog from '@/components/business/me/RediectToLoginDialog.vue'
import { defineAsyncComponent } from 'vue'
const Waterfall = defineAsyncComponent(() => import('vue-waterfall-plugin-next').then((m) => m.Waterfall))
import 'vue-waterfall-plugin-next/dist/style.css'
import { useRouter, useRoute, onBeforeRouteUpdate } from 'vue-router'
import { getCurrentUser } from '@/composables/user/users'

import { ElMessage } from 'element-plus'
import { renderMessage } from '@/utils/util'
import { getArticleAndMessage, getMemorialArticleByServiceId } from '@/composables/life/life'
import type { ArticleAndMessageResponse, MemorialArticleDTO, ArticleWrapper } from '@/composables/life/life'
import { handleErr } from '@/composables/exception/handler'
import type { CommonResponse } from '@/apis/api'
import { CODE_OK } from '@/apis/code'
import type {
  MemorialMessageDTO,
  CommentThanksCommand,
  CommentThanksDTO,
  ReportMemorialCommand,
  ClaimMemorialCommand
} from '@/composables/memorize/memorize'
import { commentThanks, report, reportMemorial, claimMemorial } from '@/composables/memorize/memorize'
import type { ReportPvCommand } from '@/composables/obituary/obituary'
import { titleBar } from '@/composables/menu/title'
import loadinImg from '@/assets/loading.png'
import errorImg from '@/assets/error.png'
import { useServicesStore } from '@/composables/memorize/store'
import { scrollToTop } from '@/utils/util'
import { isNotEmpty } from '@/utils/util'
import flowerIcon from '@/assets/juhua.jpg'
import juhua2 from '@/assets/juhua2.png'
import ArticleCard from '@/components/business/memorize/theme/ArticleCard.vue'
import AddArticleGuide from '@/components/business/memorize/empty/AddArticleGuide.vue'
import ThemeSlideContainer from '@/components/business/memorize/theme/ThemeSlideContainer.vue'
import GiftShowSlide from '@/components/business/memorize/theme/GiftShowSlide.vue'
import { useGiftStore, type GiftShortcutDTO, showGiftPlay, gifts2Play } from '@/composables/memorize/gift'
import { auditModeShow } from '@/utils/util'
import { sendUmengEvent } from '@/utils/umeng'
import { currentUserHandle } from '@/composables/user/users'
import { isSetRealName, isLogin } from '@/composables/user/users'
import PatternFallOverlay from '@/components/business/memorize/effects/PatternFallOverlay.vue'
import { useI18n } from 'vue-i18n'
import { useScheduleStore } from '@/composables/schedule/schedule'
import MemorialPageDialogs from '@/components/business/memorize/MemorialPageDialogs.vue'
import { uploadFile } from '@/composables/media/media'
import { closeLoading, openLoading } from '@/components/business/loading/loading'

import claimIcon from '@/assets/claim_icon.svg'
import reportIcon from '@/assets/report_icon.svg'

const memorialPageDialogsRef = ref<InstanceType<typeof MemorialPageDialogs> | null>(null)
const bindPhoneDialogVisible = ref(false)

const reload = inject('reload') as () => void
const giftStore = useGiftStore()
const route = useRoute()
const router = useRouter()
const { t } = useI18n()
const serviceId = ref('')
const scheduleStore = useScheduleStore()
const lastId: Ref<string | null> = ref(null)
const pageSize = ref(15)
const items: Ref<Array<MemorialMessageDTO>> = ref([])
const serviceDetailsStore = useServicesStore()
const targetMsgId = ref<string | undefined>(serviceDetailsStore.msgId || (route.query.msgId as string | undefined))
const articleParam = ref<string | undefined>(route.query.mArticle as string | undefined)

serviceId.value = route.params.id as string

// dataState is now initialized after SSR data fetch (line ~148)
const existPray = ref(true)
const isPraying = ref(true)
const hydrated = ref(false)

const isCreator = computed(() => serviceDetails.value?.creatorId === currentUserHandle.value)
const pendingGiftId = ref<string | null>(null)
const mActionOnce = {
  gift: isNotEmpty(route.query.gift),
  addMemorize: isNotEmpty(route.query.addMemorize),
  altar: isNotEmpty(route.query.altar)
}

serviceDetailsStore.clearMsgId()
if (targetMsgId.value) {
  pageSize.value = 100
}

onMounted(() => {
  hydrated.value = true
  // 重置礼物动画状态
  showGiftPlay.value = false
  gifts2Play.value = []

  console.log('serviceId.value: ', serviceId.value)
  window.addEventListener('scroll', scrollListener)
  window.addEventListener('resize', adjustFloatingButtonPosition)

  if (articleParam.value) {
    setTimeout(() => {
      window.scrollTo({ top: 252, left: 0, behavior: 'smooth' })
    }, 400)
  }

  const scheduleParams = scheduleStore.getActionDialogParams()
  if (mActionOnce.gift) {
    setTimeout(() => {
      onShowGift()
    }, 400)
  }
  if (mActionOnce.addMemorize && scheduleParams) {
    setTimeout(() => {
      router.push(
        '/addMemorize?serviceId=' +
          serviceId.value +
          '&name=' +
          scheduleParams.name +
          '&tag=' +
          scheduleParams.tag +
          '&key=' +
          scheduleParams.key
      )
    }, 400)
  }
  if (mActionOnce.altar && scheduleParams) {
    setTimeout(() => {
      router.push(
        '/altar/' +
          scheduleParams.altarId +
          '?offeringId=' +
          scheduleParams.offeringId +
          '&theme=' +
          scheduleParams.theme
      )
    }, 400)
  }

  if (import.meta.client && (route.query.gift || route.query.addMemorize || route.query.altar)) {
    if (route.query.gift) {
      removeQueryParams('gift')
    }
    if (route.query.addMemorize) {
      removeQueryParams('addMemorize')
    }
    if (route.query.altar) {
      removeQueryParams('altar')
    }
  }

  setTimeout(() => {
    adjustFloatingButtonPosition()
  }, 380)

  if (typeof window !== 'undefined' && getCurrentUser().value?.user?.id) {
    const reportPvCommand: ReportPvCommand = {
      url: route.path,
      userId: getCurrentUser().value?.user?.id
    }
    report(reportPvCommand)
  }
  // 保存 giftId 查询参数，在数据加载完成后再处理
  pendingGiftId.value = isNotEmpty(route.query.giftId) ? (route.query.giftId as string) : null

  // Hydration Check - fetch if SSR data unavailable or failed to load
  if (dataState.value !== 'done') {
    getData()
  }
})

// checkPhone
function beforePost(type: string) {
  const hasPhone = isNotEmpty(getCurrentUser().value?.user?.phone?.phoneNumber || null)
  if (hasPhone) {
    showDrawer.value = false
    //展示表单
    if (type === 'report') {
      reportDialogVisible.value = true
    } else if (type === 'claim') {
      claimDialogVisible.value = true
    }
  } else {
    bindPhoneDialogVisible.value = true
  }
}

// 举报和认领表单状态
const reportDialogVisible = ref(false)
const claimDialogVisible = ref(false)

const reportForm = reactive({
  reason: '',
  content: ''
})

const claimForm = reactive({
  relationship: '',
  identityProof: '', // 本地预览图片URL
  identityProofFile: null as File | null, // 选择的文件对象
  contactType: 'wechat',
  contactValue: ''
})

const handleClaimImageChange = (file: any) => {
  claimForm.identityProofFile = file.raw
  claimForm.identityProof = URL.createObjectURL(file.raw)
}

const submitReport = async () => {
  if (!reportForm.reason) {
    ElMessage.warning('请选择举报原因')
    return
  }
  if (!reportForm.content) {
    ElMessage.warning('请填写具体举报内容')
    return
  }

  const reportCommand: ReportMemorialCommand = {
    serviceId: serviceId.value,
    reason: reportForm.reason,
    additionMsg: reportForm.content
  }

  const { data, error, statusCode } = await reportMemorial(reportCommand)

  if (error.value) {
    handleErr(data, error, statusCode)
  } else {
    const resData = data as unknown as Ref<CommonResponse<any>>
    if (resData.value.code === CODE_OK) {
      ElMessage.success('感谢您的反馈，我们将尽快处理')
      reportDialogVisible.value = false
      reportForm.reason = ''
      reportForm.content = ''
    } else {
      ElMessage.warning(renderMessage(resData.value.message) || '举报失败')
    }
  }
}

const submitClaim = async () => {
  if (!claimForm.relationship) {
    ElMessage.warning('请选择您与逝者的关系')
    return
  }
  if (!claimForm.identityProofFile) {
    ElMessage.warning('请上传身份证明')
    return
  }
  if (!claimForm.contactValue) {
    ElMessage.warning('请填写联系方式')
    return
  }

  openLoading('正在提交认领申请...')

  try {
    let imageId = ''

    // 上传图片
    if (claimForm.identityProofFile) {
      const fileName = 'claim_identity.' + claimForm.identityProofFile.type.split('/')[1]
      const {
        data: uploadData,
        error: uploadError,
        statusCode: uploadStatusCode
      } = await uploadFile(claimForm.identityProofFile, fileName)

      if (uploadError.value) {
        closeLoading()
        handleErr(uploadData, uploadError, uploadStatusCode)
        return
      }

      if (uploadData.value?.code === CODE_OK) {
        imageId = uploadData.value.data.fileId
      } else {
        closeLoading()
        ElMessage.warning(renderMessage(uploadData.value?.message || '图片上传失败'))
        return
      }
    }

    const claimCommand: ClaimMemorialCommand = {
      serviceId: serviceId.value,
      relation: claimForm.relationship,
      contactType: claimForm.contactType,
      number: claimForm.contactValue,
      imageId: imageId
    }

    const { data, error, statusCode } = await claimMemorial(claimCommand)

    closeLoading()

    if (error.value) {
      handleErr(data, error, statusCode)
    } else {
      const resData = data as unknown as Ref<CommonResponse<any>>
      if (resData.value.code === CODE_OK) {
        ElMessage.success('如果通过审核，我们将尽快与您联系')
        claimDialogVisible.value = false
        claimForm.relationship = ''
        claimForm.identityProof = ''
        claimForm.identityProofFile = null
        claimForm.contactValue = ''
      } else {
        ElMessage.warning(renderMessage(resData.value.message) || '认领申请提交失败')
      }
    }
  } catch (e) {
    closeLoading()
    ElMessage.error('系统异常，请稍后再试')
    console.error('Claim submit error:', e)
  }
}

// SSR Data Fetching
const { data: ssrData } = await useAsyncData(`index-data-${route.params.id}`, async () => {
  const id = route.params.id as string
  const { data } = await getArticleAndMessage(id, null, pageSize.value)
  // If SSR returns permission error (10213), treat as no data to trigger client-side fallback
  if (data.value && data.value.code === 10213) {
    console.log('[SSR] Permission denied (10213) - will use client-side fallback')
    return null
  }
  // Ensure data is serializable and break any potential proxy chains
  return data.value ? JSON.parse(JSON.stringify(data.value)) : null
})

// Initialize dataState based on SSR data to prevent skeleton flash
const dataState = ref(ssrData.value && ssrData.value.code === CODE_OK ? 'done' : 'none')

const removeQueryParams = (name?: string) => {
  if (name) {
    const { [name]: _, ...rest } = route.query
    console.log('rest', rest)
    router.replace({ query: rest })
  } else {
    router.replace({ query: {} })
  }
}

onUnmounted(() => {
  // 重置礼物动画状态
  showGiftPlay.value = false
  gifts2Play.value = []

  window.removeEventListener('scroll', scrollListener)
  window.removeEventListener('resize', adjustFloatingButtonPosition)
})

function getData() {
  loadMessages(true)
}

function onRetry() {
  reload()
}

onBeforeRouteUpdate(async (to, from) => {
  // only fetch the user if the id changed as maybe only the query or the hash changed
  if (to.params.id !== from.params.id) {
    // reset
    article.value = null
    lastId.value = null
    messageCnt.value = 0
    items.value = []
    serviceId.value = ''
    located.value = false

    targetMsgId.value = route.query.msgId as string
    articleParam.value = route.query.articleParam as string

    if (targetMsgId.value) {
      pageSize.value = 100
    }
    if (articleParam.value) {
      setTimeout(() => {
        window.scrollTo({ top: 252, left: 0, behavior: 'smooth' })
      }, 400)
    }

    serviceId.value = to.params.id as string
    dataState.value = 'none'

    getData()
  }
})

const article: Ref<MemorialArticleDTO | null> = ref(null)
const articleList: Ref<Array<ArticleWrapper>> = ref([])
const showFooter = ref(true)
const footerStatus = ref('none')
const hasMoreItems = ref(true)
const messageCnt = ref(0)
const located = ref(false)
const showWaterFall = ref(true)
const showChrysanthemumFall = ref(false)
const subscribeDialogVisible = ref(false)
const giftShortcut = computed(() => giftStore.getShortcut)
const reloadWaterFall = async () => {
  showWaterFall.value = false
  await nextTick()
  showWaterFall.value = true
}

const lifeAndArticle = ref<ArticleAndMessageResponse>()

// 标记页面数据是否已完成加载
const dataLoaded = ref(false)

async function loadMessages(isInitial: boolean = false, last: string | null = null) {
  if (footerStatus.value === 'loading') {
    return
  }

  if (isInitial) {
    lastId.value = null
    if (dataState.value === 'none') {
      // nextTick(() => {
      dataState.value = 'loading'
      // })
    }
  }

  footerStatus.value = 'loading'

  const { data, error, statusCode } = await getArticleAndMessage(serviceId.value, last, pageSize.value)
  console.log('dataasdas: ', data)
  if (error.value) {
    console.error('err:', error)
    //handleErr(data, error, statusCode)

    if (last === null) {
      dataState.value = 'error'
    } else {
      footerStatus.value = 'failed'
    }
  } else {
    const resData = data as unknown as Ref<CommonResponse<ArticleAndMessageResponse>>

    if (resData.value.code === CODE_OK) {
      // success
      const response: ArticleAndMessageResponse = resData.value.data
      lifeAndArticle.value = response
      console.log('请求等到的所有数据！！！：', lifeAndArticle.value.items)
      let delayTime = 0
      if (last === null) {
        items.value = []
        articleList.value = resData.value.data.articles
        dataState.value = 'done'
        dataLoaded.value = true
        reloadWaterFall()
        delayTime = 300
      }
      setTimeout(() => {
        if (pendingGiftId.value) {
          giftPanelVisible.value = true
          pendingGiftId.value = null
        }
      }, 1000)
      setTimeout(() => {
        if (items.value) {
          // 添加空值检查
          items.value = [...items.value, ...response.items]
        }
        messageCnt.value = response.total
        footerStatus.value = 'none'
        hasMoreItems.value = true
        if (response.items.length === 0) {
          hasMoreItems.value = false
        } else {
          const newLastId = response.items[response.items.length - 1].id
          if (last !== null && newLastId === last) {
            hasMoreItems.value = false
          } else {
            lastId.value = newLastId
          }
        }
        const foundMsg = items.value.find((item) => item.id === targetMsgId.value)
        if (foundMsg && targetMsgId.value && !located.value) {
          toLocate(route.query.noti !== 'true' ? 400 : 200)
          if (route.query.noti !== 'true') {
            setTimeout(() => {
              subscribeDialogVisible.value = true
            }, 2000)
          }
        }
      }, delayTime)
    } else {
      ElMessage({
        message: renderMessage(resData.value.message),
        type: 'warning'
      })

      if (last === null) {
        dataState.value = 'error'
      } else {
        footerStatus.value = 'failed'
      }
    }
  }
}

const playAnim = ref(false)

function toLocate(offset: number = 220) {
  setTimeout(() => {
    if (targetMsgId.value && !located.value) {
      console.log('判断通过，调用findAndScroll')
      findAndScroll(targetMsgId.value, 220)
    }
  }, 500)
}

const itemRefs: Map<string, HTMLElement> = new Map()
const waterfallRef: Ref<typeof Waterfall> = ref()
const contentContainer: Ref<HTMLElement | null> = ref(null)
const floatingButton: Ref<HTMLElement | null> = ref(null)

const floatingButtonStyle = ref<{ marginRight?: string }>({
  marginRight: '1rem'
})

const adjustFloatingButtonPosition = () => {
  if (contentContainer.value && floatingButton.value) {
    const contentRect = contentContainer.value.getBoundingClientRect()
    const windowWidth = window.innerWidth
    const rightDistance = windowWidth - (contentRect.right + 16 * 15) // 7rem 转换为 px
    if (rightDistance > 0) {
      floatingButtonStyle.value.marginRight = `${rightDistance}px`
    } else {
      floatingButtonStyle.value.marginRight = '0.5rem'
    }
  } else {
    floatingButtonStyle.value.marginRight = '0.5rem'
  }
}

// Function to set references to the DOM elements
const setItemRef = (el: Element | ComponentPublicInstance | null, id: string): void => {
  // Ensure the ref is an HTMLElement
  if (el && '$el' in el) {
    el = (el as ComponentPublicInstance).$el as HTMLElement
  }
  if (el instanceof HTMLElement) {
    itemRefs.set(id, el)
  } else {
    itemRefs.delete(id)
  }
}
//
const findAndScroll = (key: string, offset: number = 0) => {
  const element = itemRefs.get(key)
  console.log('需要使用时itemRefs: ', itemRefs)
  if (element) {
    const elementTop = element.getBoundingClientRect().top
    const scrollPosition = window.scrollY + elementTop - offset
    window.scrollTo({
      top: scrollPosition,
      behavior: 'smooth'
    })
    located.value = true
    // 触发菊花掉落动画蒙层（不拦截交互）
    // Overlay 内部存在延迟释放窗口（spawnWindowMs，默认 ~4000ms）、下落时长（durationMs）、落底后停留（holdAfterLandMs，默认 ~400ms）与淡出（fadeOutMs，默认 ~600ms）
    // 之前固定 6000ms 过早清理，导致尚未开始的元素被清空，看起来“动画不展示”。
    // 这里按总时长计算隐藏时间，确保完整播放。
    const overlayHideMs =
      6000 /* durationMs 传入 */ +
      4000 /* spawnWindowMs 默认 */ +
      400 /* holdAfterLandMs 默认 */ +
      600 /* fadeOutMs 默认 */
    showChrysanthemumFall.value = true
    setTimeout(() => {
      showChrysanthemumFall.value = false
    }, overlayHideMs)
    setTimeout(() => {
      // 可以在这里使用我们的新函数来添加背景高亮效果
      // addTemporaryBackground('#FCF890', 1500, element)
      // 但我们保留原有的动画效果，这只是一个示例
      playAnim.value = true
    }, 600)
  } else {
    // 兜底：未找到元素（可能尚未渲染完瀑布流），也触发一次掉落动画
    const overlayHideMs = 6000 + 4000 + 400 + 600
    showChrysanthemumFall.value = true
    setTimeout(() => {
      showChrysanthemumFall.value = false
    }, overlayHideMs)
  }
}

const scrollListener = function () {
  // Get the current scroll position
  const scrollTop = window.pageYOffset || document.documentElement.scrollTop
  // Get the maximum scroll height of the document
  const scrollHeight = document.documentElement.scrollHeight
  // Get the height of the viewport
  const clientHeight = document.documentElement.clientHeight
  // Check if the user has scrolled to the bottom
  if (scrollTop + clientHeight + 10 >= scrollHeight) {
    if (hasMoreItems.value) {
      loadMessages(false, lastId.value)
    }
  }
}

function tryAgain() {
  if (footerStatus.value === 'failed') {
    if (items.value.length == 0) {
      loadMessages(true)
    } else {
      loadMessages(false, lastId.value)
    }
  }
}

function handleDelete(msgId: string) {
  const count = items.value.length
  items.value = items.value.filter((item) => item.id !== msgId)
  if (items.value.length < count) {
    messageCnt.value--
  }
}

function addMemory() {
  router.push('/addMemorize?serviceId=' + serviceId.value + '&name=' + titleBar.value?.life?.name.fullName)
}

const options = reactive({
  // 唯一key值
  rowKey: 'id',
  // 卡片之间的间隙
  gutter: 12,
  // 是否有周围的gutter
  hasAroundGutter: false,
  // 卡片在PC上的宽度
  width: 380,
  // 自定义行显示个数，主要用于对移动端的适配
  breakpoints: {
    3000: {
      rowPerView: 1
    }
  },
  // 动画效果
  animationEffect: 'animate__fadeInUp',
  // 动画时间
  animationDuration: 500,
  // 动画延迟
  animationDelay: 200,
  // 背景色
  backgroundColor: 'transparent',
  // imgSelector
  imgSelector: 'src',
  // crossOrigin
  crossOrigin: true,
  // 加载配置
  loadProps: {
    loadinImg,
    errorImg,
    ratioCalculator: (width: number, height: number) => {
      // 我设置了最小宽高比
      const minRatio = 1 / 10
      const maxRatio = 4 / 1
      // 获取当前图片的比例
      const curRatio = width / height
      // 如果当前图片比列不在此范围内，我们取最小或者最大值
      if (curRatio < minRatio) {
        return minRatio
      } else if (curRatio > maxRatio) {
        return maxRatio
      } else {
        return curRatio
      }
    }
  },
  // 是否懒加载
  lazyload: true,
  align: 'center'
})

const dataLoading = ref(true)

function afterRender() {
  dataLoading.value = false
  nextTick(() => {
    adjustFloatingButtonPosition()

    // 在瀑布流渲染完成后，如果有待处理的 giftId，再打开礼物面板
  })
}

class ViewCardItem {
  file: MemorialMessageDTO
  src: string
  name: string

  constructor(_file: MemorialMessageDTO) {
    this.file = _file
    this.src = _file.gift?.file?.url || _file.gift?.file?.middleQuality || ''
    this.name = _file.gift?.giftName || ''
  }
}

const comments = computed(() => items.value.map((item) => new ViewCardItem(item)))

const serviceDetails = computed(() => serviceDetailsStore.searchForItem(serviceId.value))

const canComment = computed(() => {
  const permission = serviceDetails.value?.privilege?.permissions.find((item) => item.name === 'm_message')
  return permission?.value && permission.value !== 'READ'
})

const isClientAllowed = computed(() => {
  if (import.meta.server || !inMiniprograme.value) {
    return true
  }

  const currentUserId = getCurrentUser().value?.user?.id
  if (serviceDetails.value?.creator?.id === currentUserId) {
    return true
  }

  const hasInvitedPermission = serviceDetails.value?.privilege?.permissions.some((item) => item.value !== 'NONE')
  if (hasInvitedPermission) {
    return true
  }

  if (serviceDetails.value?.life?.openToPub === true) {
    return false
  }

  return true
})

const isPublicFigure = computed(() => serviceDetails.value?.life?.publicFigure === true)

const canManageComment = computed(() => {
  const permission = serviceDetails.value?.privilege?.permissions.find((item) => item.name === 'm_message')
  if (serviceDetails.value?.privilege == null) {
    return false
  } else {
    return permission?.value && permission.value === 'MANAGE'
  }
})
function onAfterLocate() {
  playAnim.value = false
  targetMsgId.value = undefined
}

async function onAddMemory() {
  if (!isSetRealName.value) {
    const isSuccess = await memorialPageDialogsRef.value?.openRealNameDialog()
    if (!isSuccess) {
      console.log('用户取消了实名认证，停止后续操作')
      return
    }
  }
  router.push('/addMemorize?serviceId=' + serviceId.value + '&name=' + serviceDetails.value.life.name.fullName)
  sendUmengEvent('clk_create_m', {})
}

function on2CrateLife() {
  router.push('/guide/createLifeAndBaseInfo?redirect=true')
}

const giftPanelVisible = ref(false)
const giftCategoryInit = ref<string | null>(null)
const giftIdInit = ref<string | null>(null)
function onGiftSent() {
  getData()
}

const canArticle = computed(() => {
  const permission = serviceDetails?.value?.privilege?.permissions.find((item) => item.name === 'm_article')

  return permission?.value && permission.value !== 'READ'
})

function onArticleChanged() {
  getData()
}

const giftSlideRef = ref<InstanceType<typeof GiftShowSlide> | null>(null)

function locateGift() {
  const rect = giftSlideRef.value?.locate()
  if (rect) {
    const elementTop = rect.top
    const scrollPosition = window.scrollY + elementTop - 20
    window.scrollTo({
      top: scrollPosition,
      behavior: 'smooth'
    })

    // 触发菊花掉落动画
    showChrysanthemumFall.value = true
    const overlayHideMs = 6000 + 4000 + 400 + 600 // durationMs + spawnWindowMs + holdAfterLandMs + fadeOutMs
    setTimeout(() => {
      showChrysanthemumFall.value = false
    }, overlayHideMs)
  }
}

function onShowGift() {
  if (!isLogin.value) {
    console.log('拦截到敏感操作，跳转登录页')
    showRedirectToLoginDialog.value = true
    return
  }
  scrollToTop()
  giftCategoryInit.value = 'flower'
  giftPanelVisible.value = true
  sendUmengEvent('clk_gift', {})
}

function onShortcutClick(shortcut: GiftShortcutDTO) {
  if (!isLogin.value) {
    showRedirectToLoginDialog.value = true
    return
  }
  scrollToTop()
  giftCategoryInit.value = shortcut.giftCategory
  giftPanelVisible.value = true
  sendUmengEvent('clk_festival_btn', {})
}

// 专门用于刷新文章内容的方法
async function refreshArticle() {
  if (!serviceId.value) return

  const { data, error, statusCode } = await getMemorialArticleByServiceId(serviceId.value)

  if (error.value) {
    handleErr(data, error, statusCode)
  } else {
    const resData = data as unknown as Ref<CommonResponse<MemorialArticleDTO>>
    if (resData.value.code === CODE_OK) {
      // 直接更新文章内容
      article.value = resData.value.data
      console.log('文章内容已刷新')
    } else {
      ElMessage({
        message: renderMessage(resData.value.message),
        type: 'warning'
      })
    }
  }
}

const showDrawer = ref(false)

const onModifyOpen = () => {
  showDrawer.value = true
  //router.push('/guide/setOpenToPub?edit=true&lifeId=' + titleBar.value?.life?.id)
}

let innerWidth = ref(0)
if (typeof window !== 'undefined') {
  innerWidth.value = window.innerWidth
}

const isExpandable = computed(() => {
  return innerWidth.value <= 768 // 移动端可展开，PC端不可展开
})
const showThankDialog = ref(false)

const thankMessageId = ref<string | null>(null)
const thankMessageName = ref<string | null>(null)
function onThank(msgId: string, name: string) {
  showThankDialog.value = true
  thankMessageName.value = name
  thankMessageId.value = msgId
}

async function onSentThank(content: string, name: string, messageId: string) {
  const commentThanksCommand: CommentThanksCommand = {
    messageId: messageId,
    contentText: content,
    thankName: name
  }
  const { data, error, statusCode } = await commentThanks(commentThanksCommand)
  if (error.value) {
    handleErr(data, error, statusCode)
  } else {
    const resData = data as unknown as Ref<CommonResponse<any>>
    if (resData.value.code === CODE_OK) {
      ElMessage.success(t('obituary.thank_success'))
      const thank = resData.value.data.thanks as CommentThanksDTO
      const index = items.value.findIndex((item) => item.id === thank.messageId)
      console.log('留言列表', lifeAndArticle.value.items)
      if (index === -1) {
        console.log('留言消失', resData.value.data)
        ElMessage.error(t('m.thank_error'))
      } else {
        items.value[index].thanks = thank
        items.value[index].thanksId = thank.id
        showThankDialog.value = false
      }
    } else {
      console.log('code不对', resData.value.data)

      ElMessage.error(resData.value.message)
    }
  }
}

const showCheckThankDialog = ref(false)
const selectedThank = ref<CommentThanksDTO | null>(null)
function onCheckThank(thank: CommentThanksDTO) {
  selectedThank.value = thank
  showCheckThankDialog.value = true
}
const showRedirectToLoginDialog = ref(false)
const GiftShowSlideClick = () => {
  if (!isLogin.value) {
    showRedirectToLoginDialog.value = true
    return
  }
  giftPanelVisible.value = true
}

// Locate SSR Data Handler definition here to ensure all refs are available
function handleSsrData() {
  if (!ssrData.value) return
  const resData = ssrData.value
  if (resData.code === CODE_OK) {
    const response: ArticleAndMessageResponse = resData.data
    lifeAndArticle.value = response
    items.value = response.items || []
    articleList.value = response.articles
    dataState.value = 'done'
    dataLoaded.value = true
    messageCnt.value = response.total
    footerStatus.value = 'none'
    hasMoreItems.value = true

    if (response.items.length === 0) {
      hasMoreItems.value = false
    } else {
      if (items.value.length > 0) {
        lastId.value = items.value[items.value.length - 1].id
      }
    }

    // Handle delayed logic that depends on data being present - only runs on client anyway due to setTimeout
    setTimeout(() => {
      if (pendingGiftId.value) {
        giftPanelVisible.value = true
        pendingGiftId.value = null
      }
    }, 1000)

    setTimeout(() => {
      // Locate message if needed
      const foundMsg = items.value.find((item) => item.id === targetMsgId.value)
      if (foundMsg && targetMsgId.value && !located.value) {
        toLocate(route.query.noti !== 'true' ? 400 : 200)
        if (route.query.noti !== 'true') {
          setTimeout(() => {
            subscribeDialogVisible.value = true
          }, 2000)
        }
      }
    }, 300)
  }
}

// Execute on Server (setup) or Client (hydration)
if (ssrData.value && ssrData.value.code === CODE_OK) {
  handleSsrData()
}

const pageTitle = computed(() => {
  const fullName = serviceDetails.value?.life?.name?.fullName
  if (auditModeShow.value) {
    return t('m.tag_title_audit', { fullName: fullName })
  } else if (inMiniprograme.value) {
    return t('m.tag_title_mini', { fullName: fullName })
  }
  return fullName ? t('m.memorial_hall_title', { name: fullName }) : t('m.memorial_hall_default')
})

const requestURL = useRequestURL()
const canonicalUrl = computed(() => `${requestURL.origin}${route.path}`)

useSeoMeta({
  title: pageTitle,
  ogTitle: pageTitle,
  description: () => t('m.memorize_desc', { name: serviceDetails.value?.life?.name?.fullName }),
  ogDescription: () => t('m.memorize_desc', { name: serviceDetails.value?.life?.name?.fullName }),
  ogImage: () => serviceDetails.value?.life?.avatar?.url || serviceDetails.value?.life?.avatar?.middleQuality,
  ogUrl: canonicalUrl,
  twitterCard: 'summary_large_image'
})

useHead({
  link: [
    {
      rel: 'canonical',
      href: canonicalUrl
    }
  ]
})

const subscribeUrl = ref('')
const actionTxt = ref('')

onMounted(() => {
  const urlObj = new URL(window.location.href)
  urlObj.searchParams.set('redirect', 'true')
  subscribeUrl.value = urlObj.toString()
  actionTxt.value = '返回' + (route.meta.title || '')
})
</script>
<template>
  <BaseLayout :state="dataState" @retry="onRetry">
    <div ref="contentContainer" relative w-full mt-1 flex="~ col" items-center>
      <ArticleCard
        :articleList="articleList"
        :privilege="serviceDetails?.privilege"
        :serviceId="serviceId"
        :creatorId="serviceDetails?.creatorId"
        :life="serviceDetails?.life"
        :largeMode="isPublicFigure"
        @changed="onArticleChanged">
      </ArticleCard>
      <!-- 空占位 -->
      <ClientOnly>
        <div v-if="!auditModeShow && isClientAllowed" class="w-full">
          <GiftShowSlide
            ref="giftSlideRef"
            :serviceId="serviceId"
            :canManage="canManageComment || false"
            @locate="locateGift"
            @onShowGift="GiftShowSlideClick" />
        </div>
      </ClientOnly>
      <!-- <ThemeSlideContainer mt-6 :serviceId="serviceId" :fullName="titleBar.life?.name.fullName || ''"
          :lifeAndArticle="lifeAndArticle" :canManage="canManageComment || false"
   /> -->
      <div w-full flex="~ col" px-1>
        <ClientOnly>
          <div flex="~ row" mt-8 mb-4 items-center justify-between text-theme-dark>
            <h2 text-xl font-semibold>
              {{
                auditModeShow
                  ? t('m.title_memorize_audit', { count: messageCnt })
                  : t('m.title_memorize', { count: messageCnt })
              }}
            </h2>
          </div>
        </ClientOnly>
        <ClientOnly>
          <template #default>
            <div v-if="comments && comments.length > 0 && showWaterFall" class="w-full">
              <Waterfall
                ref="waterfallRef"
                :list="comments"
                :row-key="options.rowKey"
                :gutter="options.gutter"
                :has-around-gutter="options.hasAroundGutter"
                :width="options.width"
                :breakpoints="options.breakpoints"
                :img-selector="options.imgSelector"
                :background-color="options.backgroundColor"
                :animation-effect="options.animationEffect"
                :animation-duration="options.animationDuration"
                :animation-delay="options.animationDelay"
                :lazyload="options.lazyload"
                :load-props="options.loadProps"
                :cross-origin="options.crossOrigin"
                :align="options.align"
                @afterRender="afterRender">
                <template #default="{ item, url, index }">
                  <MemorizeComment
                    :item="item.file"
                    :key="item.file.id"
                    :ref="(el: any, _refs: any) => setItemRef(el, item.file.id)"
                    @onDelete="handleDelete"
                    @animationEnd="onAfterLocate"
                    :canManage="canManageComment"
                    :anim="playAnim && item.file.id == targetMsgId"
                    @onThank="onThank"
                    @onCheckThank="onCheckThank" />
                </template>
              </Waterfall>
            </div>
          </template>
          <template #fallback>
            <div v-if="comments && comments.length > 0" class="columns-2 md:columns-3 gap-2 px-1">
              <div v-for="item in comments" :key="item.file.id" class="mb-2 break-inside-avoid">
                <div class="relative rounded-lg overflow-hidden bg-white shadow-sm">
                  <img v-if="item.src" :src="item.src" class="w-full h-auto object-cover" />
                  <div class="p-2 text-sm">{{ item.name }}</div>
                </div>
              </div>
            </div>
          </template>
        </ClientOnly>

        <!-- 空占位 -->
        <div v-if="items.length === 0 && footerStatus == 'none'" w-full flex-l-center py-20 mb-8 text-second>
          <div v-if="hydrated && canComment" flex="~ row" items-center justify-center my-3 cursor-pointer>
            <div
              p-2
              px-4
              rounded-full
              border-1
              border-theme-main
              hover:border-theme-main
              text-theme-main
              flex="~ row"
              items-center
              justify-center
              @click="onAddMemory"
              opacity-85
              text-sm>
              {{ $t('empty.send_comment') }}
            </div>
          </div>
          <div v-if="!canComment" mb-2 mt-8 px-8 py-2 text-second>{{ $t('m.msg_empty') }}</div>
        </div>
        <MFooter
          v-if="items.length > 0"
          v-model="showFooter"
          :loading="footerStatus"
          :hasMore="hasMoreItems"
          @click="tryAgain" />
      </div>
    </div>
    <template #skeleton>
      <MemorizeSkeleton />
    </template>

    <RAction
      v-model="showDrawer"
      :fromBottom="true"
      :mask="true"
      :expandable="isExpandable"
      :heightPercent="30"
      :backdropHide="true">
      <div w-full h-full bg-white px-5 py-10 relative rounded-t-2>
        <!-- <div bg-gray-light class="left-50% transform -translate-x-1/2 w-20 h-1 absolute top-3 rounded-full" /> -->
        <div absolute top-0 right-0 p-4 z-1 @click.stop.prevent="showDrawer = false">
          <div i-lets-icons:expand-down-light class="w-6 h-6" />
        </div>

        <div
          flex
          justify-start
          items-center
          cursor-pointer
          w-full
          py-2
          v-if="isCreator"
          mb-3
          @click="router.push('/guide/setOpenToPub?edit=true&lifeId=' + titleBar?.life?.id)">
          <div class="i-icon-park-outline:personal-privacy w-2em h-2em" style="color: #000000" mr-4></div>
          <div flex flex-col items-start justify-center w-full>
            <div text-lg font-bold>{{ $t('m.privacy') }}</div>
            <div text-sm text-second>{{ $t('m.privacy_desc') }}</div>
          </div>
        </div>

        <div
          flex
          justify-start
          items-center
          cursor-pointer
          w-full
          py-2
          v-if="!isCreator"
          mb-3
          @click="beforePost('report')">
          <img :src="reportIcon" style="width: 2em; height: 2em" mr-4 />
          <div flex flex-col items-start justify-center w-full>
            <div text-lg font-bold>{{ $t('m.report') }}</div>
          </div>
        </div>

        <div
          flex
          justify-start
          items-center
          cursor-pointer
          w-full
          py-2
          v-if="!isCreator && serviceDetails?.life.publicFigure"
          @click="beforePost('claim')">
          <img :src="claimIcon" style="width: 2em; height: 2em" mr-4 />
          <div flex flex-col items-start justify-center w-full>
            <div text-lg font-bold>{{ $t('m.claim') }}</div>
          </div>
        </div>
      </div>
    </RAction>

    <!-- 悬浮按钮 -->
    <div
      fixed
      w-full
      max-w-screen-lg
      m-auto
      bottom-2
      right-2
      min-h-14
      py-2
      pb-3
      z-1
      class="safe-padding floating-button"
      style="pointer-events: none">
      <div w-full flex flex-row justify-right items-center mb-1 :style="floatingButtonStyle">
        <!-- 修改开放按钮 -->
        <div mb-3>
          <div
            v-if="hydrated"
            flex
            justify-center
            items-center
            rounded-full
            btn-theme-solid
            text-inverted
            backdrop-blur-sm
            shadow-md
            p-0
            w-10
            h-10
            @click="onModifyOpen"
            style="pointer-events: auto">
            <div class="i-bi:three-dots w-1.7em h-1.7em" style="color: #ffffff"></div>
          </div>
        </div>
      </div>
      <div
        ref="floatingButton"
        w-full
        flex
        flex-row
        justify-between
        md:justify-right
        items-center
        mb-1
        :style="floatingButtonStyle">
        <div></div>

        <div flex="~ row">
          <div
            v-if="isPublicFigure && !canComment"
            flex="~ row"
            items-center
            justify-center
            btn-theme-solid
            rounded-full
            shadow-md
            cursor-pointer
            ml-2.5
            px-2
            py-1.5
            @click="on2CrateLife"
            style="pointer-events: auto">
            <div i-basil:plus-outline mx-1 w-6 h-6></div>
            <div mr-1 ml-0.5 md:text-sm>
              {{ inMiniprograme ? $t('action.create_my_m_space') : $t('action.create_my_memorial') }}
            </div>
          </div>
          <div
            v-if="
              hydrated &&
              giftShortcut &&
              giftShortcut.file &&
              isNotEmpty(giftShortcut.name) &&
              !auditModeShow &&
              isClientAllowed &&
              (!isPublicFigure || canComment)
            "
            flex="~ row"
            items-center
            justify-center
            btn-theme-outline
            shadow-md
            pl-0
            pb-0
            pt-0
            rounded-full
            @click="onShortcutClick(giftShortcut)"
            px-2
            py-1.5
            overflow-hidden
            style="pointer-events: auto">
            <div relative w-9 h-7 mr-2 ml--2 mb--1>
              <img v-if="giftShortcut?.file?.url" :src="giftShortcut?.file?.url" w-9 h-9 absolute top--1 left-1 />
            </div>
            <div mr-1 ml-0.5 md:text-sm>{{ giftShortcut?.name }}</div>
          </div>

          <!-- 献花按钮 -->
          <div
            v-if="hydrated && !auditModeShow && isClientAllowed"
            flex="~ row"
            items-center
            justify-center
            btn-theme-solid
            backdrop-blur-sm
            shadow-md
            rounded-full
            @click="onShowGift"
            px-2
            ml-2.5
            py-1.5
            max-w-40
            style="pointer-events: auto">
            <div relative w-7 h-7 mr-2>
              <img :src="flowerIcon" w-7 h-7 mx-2px />
            </div>
            <div mr-1 ml-0.5 md:text-sm>
              {{ isPublicFigure && !canComment ? $t('action.add_flower_for_free') : $t('action.add_gift') }}
            </div>
          </div>

          <div
            v-if="hydrated && canComment"
            flex="~ row"
            items-center
            justify-center
            btn-theme-outline
            rounded-full
            shadow-md
            cursor-pointer
            ml-2.5
            px-2
            py-1.5
            @click="onAddMemory"
            max-w-34
            style="pointer-events: auto">
            <div i-basil:edit-alt-outline mx-1 w-6 h-6></div>
            <div mr-1 ml-0.5 md:text-sm>{{ $t('gift.msg') }}</div>
          </div>
        </div>
      </div>
    </div>
    <ClientOnly>
      <div>
        <GiftPanel
          v-model="giftPanelVisible"
          :serviceId="serviceId"
          :initCategory="giftCategoryInit"
          :altarId="serviceDetails?.life?.altar?.id || null"
          :freeFlowerOnly="isPublicFigure && !canComment"
          @sent="onGiftSent" />
      </div>
    </ClientOnly>
    <!-- 菊花掉落动画蒙层（透明、不拦截交互） -->
    <PatternFallOverlay :show="showChrysanthemumFall" :count="36" :durationMs="6000" :sizePx="48" :imgSrc="juhua2" />
    <ClientOnly>
      <div v-if="!getCurrentUser().value?.user?.subscribed && isLogin">
        <SubscribeDrawer
          v-model="subscribeDialogVisible"
          tipsText="接收相关纪念消息"
          :content="'您已成功关注该纪念馆：'"
          :url="subscribeUrl"
          :actionTxt="actionTxt" />
      </div>
    </ClientOnly>
    <ThankDialog
      v-model="showThankDialog"
      :messageName="thankMessageName"
      :messageId="thankMessageId"
      @onSentThank="onSentThank" />
    <ReviceThankDialog v-model="showCheckThankDialog" :thank="selectedThank" @onCheckThank="onCheckThank" />
    <MemorialPageDialogs
      ref="memorialPageDialogsRef"
      v-model:bindPhoneDialogVisible="bindPhoneDialogVisible"
      v-model:reportDialogVisible="reportDialogVisible"
      v-model:claimDialogVisible="claimDialogVisible"
      v-model:reportForm="reportForm"
      v-model:claimForm="claimForm"
      :serviceName="serviceDetails?.life?.name?.fullName || ''"
      @submitReport="submitReport"
      @submitClaim="submitClaim"
      @claimImageChange="handleClaimImageChange" />

    <RediectToLoginDialog v-model="showRedirectToLoginDialog" />
  </BaseLayout>
</template>

<style lang="scss" scoped>
/* 自定义 radio 样式 */
:deep(.el-radio) {
  --el-radio-text-color: #333;
}

:deep(.el-radio__input.is-checked + .el-radio__label) {
  color: #333 !important;
}

:deep(.el-radio__input.is-checked .el-radio__inner) {
  border-color: #f0ba02 !important;
  background-color: transparent !important;
}

:deep(.el-radio__inner::after) {
  width: 6px !important;
  height: 6px !important;
  background-color: #f0ba02 !important;
}

:deep(.el-radio__input.is-checked .el-radio__inner::after) {
  background-color: #f0ba02 !important;
  transform: translate(-50%, -50%) scale(1) !important;
}

:deep(.avatar-uploader .el-upload) {
  border: 1px dashed var(--el-border-color);
  border-radius: 6px;
  cursor: pointer;
  position: relative;
  overflow: hidden;
  transition: var(--el-transition-duration-fast);
}

:deep(.avatar-uploader .el-upload:hover) {
  border-color: var(--el-color-primary);
}

/* 定义星星闪烁的动画关键帧 */
@keyframes twinkle {
  0% {
    transform: scale(0.5);
    opacity: 0.1;
  }

  50% {
    transform: scale(1.2);
    opacity: 1;
  }

  100% {
    transform: scale(0.5);
    opacity: 0.1;
  }
}

/* 绘制星星的光芒 */
.star-glow {
  position: absolute;
  top: 0px;
  right: 0px;
  width: 10px;
  height: 10px;
  animation: twinkle 4s infinite;
  z-index: 10;
}

@supports (bottom: constant(safe-area-inset-bottom)) or (bottom: env(safe-area-inset-bottom)) {
  .safe-padding {
    padding-bottom: constant(safe-area-inset-bottom);
    padding-bottom: env(safe-area-inset-bottom);
    transition: padding-bottom 0.3s ease;
  }
}
</style>
